"use client";

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import Link from "next/link";
import type { HeroHeadline, HeroScene } from "@/content/home-hero";
import { HeroSceneArt, HeroTabIcon } from "@/components/site/HeroScenes";

export interface HeroSlideView {
  key: string;
  scene: HeroScene;
  tabLabel: string;
  kicker: string;
  hook: string;
  sub: string;
  chips: string[];
  primaryLabel: string;
  primaryHref: string;
  secondaryCta: { label: string; href: string };
}

const INTERVAL_MS = 7000;

/**
 * Hero da home: H1 fixo (SEO) + slider de serviços com cena animada, abas com
 * ícone animado e barra de progresso. Acessível: abas WAI-ARIA (setas, Home/End),
 * autoplay pausa no hover, no foco, fora da tela e com a aba do navegador oculta;
 * sem autoplay com prefers-reduced-motion. Todo o texto dos slides fica no HTML
 * (indexável); os inativos ficam `inert` e escondidos visualmente.
 */
export function HeroSlider({
  telemetry,
  headline,
  slides,
  micro,
  leadForm,
}: {
  telemetry: string;
  headline: HeroHeadline;
  slides: HeroSlideView[];
  micro: string[];
  leadForm?: ReactNode;
}) {
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(true);
  const [docHidden, setDocHidden] = useState(false);
  const [reduced, setReduced] = useState(false);
  const rootRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const pointerX = useRef<number | null>(null);
  const count = slides.length;
  const paused = hovered || focused || !visible || docHidden || reduced || count < 2;

  const go = useCallback((i: number) => setActive(((i % count) + count) % count), [count]);

  // Ambiente: reduced-motion, visibilidade da aba e da hero (subscriptions; setState só nos callbacks).
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onMq = () => setReduced(mq.matches);
    const onVis = () => setDocHidden(document.visibilityState === "hidden");
    const raf = requestAnimationFrame(() => {
      onMq();
      onVis();
    });
    mq.addEventListener("change", onMq);
    document.addEventListener("visibilitychange", onVis);
    let io: IntersectionObserver | undefined;
    if (rootRef.current && "IntersectionObserver" in window) {
      io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.25 });
      io.observe(rootRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      mq.removeEventListener("change", onMq);
      document.removeEventListener("visibilitychange", onVis);
      io?.disconnect();
    };
  }, []);

  // Autoplay: um timer por slide ativo.
  useEffect(() => {
    if (paused) return;
    const t = window.setTimeout(() => go(active + 1), INTERVAL_MS);
    return () => window.clearTimeout(t);
  }, [active, paused, go]);

  // SMIL (pacotes do hub ERP) também respeita reduced-motion.
  useEffect(() => {
    rootRef.current?.querySelectorAll<SVGSVGElement>("svg.hx-svg").forEach((svg) => {
      if (reduced) svg.pauseAnimations?.();
      else svg.unpauseAnimations?.();
    });
  }, [reduced]);

  function onTabKey(e: KeyboardEvent<HTMLButtonElement>) {
    let next: number | null = null;
    if (e.key === "ArrowRight") next = active + 1;
    else if (e.key === "ArrowLeft") next = active - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = count - 1;
    if (next === null) return;
    e.preventDefault();
    const idx = ((next % count) + count) % count;
    go(idx);
    tabRefs.current[idx]?.focus();
  }

  return (
    <section
      ref={rootRef}
      className={`hx${paused ? " is-paused" : ""}`}
      aria-roledescription="carrossel"
      aria-label="Serviços em destaque"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
      style={{ ["--hx-dur" as string]: `${INTERVAL_MS}ms` }}
    >
      <div className="hx__bg" aria-hidden>
        <div className="hx__grid" />
        <div className="hx__glow hx__glow--a" />
        <div className="hx__glow hx__glow--b" />
      </div>

      <div className="container hx__inner">
        <div className="hx__copy">
          {telemetry ? (
            <div className="hx__kicker">
              <span className="hx__pulse" aria-hidden />
              {telemetry}
            </div>
          ) : null}

          <h1 className="hx__title">
            {headline.pre} <span className="hx__grad">{headline.grad}</span> {headline.post}
          </h1>

          <div className="hx__slides">
            {slides.map((s, i) => (
              <div
                key={s.key}
                id={`hx-panel-${i}`}
                role="tabpanel"
                aria-labelledby={`hx-tab-${i}`}
                className={`hx-copy${i === active ? " is-active" : ""}`}
                inert={i !== active}
              >
                <p className="hx-copy__kicker">{s.kicker}</p>
                <p className="hx-copy__hook">{s.hook}</p>
                <p className="hx-copy__sub">{s.sub}</p>
                {s.chips.length ? (
                  <ul className="hx-copy__chips">
                    {s.chips.map((c) => (
                      <li key={c}>
                        <svg viewBox="0 0 16 16" aria-hidden>
                          <path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {c}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="hx-copy__ctas">
                  <a className="btn btn--primary btn--lg btn-magnetic" href={s.primaryHref} target="_blank" rel="noopener" data-track={`hero-${s.key}-whatsapp`}>
                    {s.primaryLabel} <span className="arrow">→</span>
                  </a>
                  {s.secondaryCta.label && s.secondaryCta.href ? (
                    <Link className="btn btn--ghost btn--lg" href={s.secondaryCta.href}>
                      {s.secondaryCta.label}
                    </Link>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          {micro.length ? (
            <ul className="hx__micro">
              {micro.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="hx__visual">
          <div
            className="hx__stage"
            onPointerDown={(e) => {
              pointerX.current = e.clientX;
            }}
            onPointerUp={(e) => {
              if (pointerX.current === null) return;
              const dx = e.clientX - pointerX.current;
              pointerX.current = null;
              if (Math.abs(dx) > 40) go(active + (dx < 0 ? 1 : -1));
            }}
          >
            {slides.map((s, i) => (
              <div key={s.key} className={`hx-scene hx-scene--${s.scene}${i === active ? " is-active" : ""}`} aria-hidden={i !== active}>
                <HeroSceneArt scene={s.scene} />
              </div>
            ))}
          </div>

          <div className="hx__tabs" role="tablist" aria-label="Escolha o serviço">
            {slides.map((s, i) => (
              <button
                key={s.key}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                id={`hx-tab-${i}`}
                type="button"
                role="tab"
                aria-selected={i === active}
                aria-controls={`hx-panel-${i}`}
                tabIndex={i === active ? 0 : -1}
                className={`hx-tab${i === active ? " is-active" : ""}`}
                onClick={() => go(i)}
                onKeyDown={onTabKey}
              >
                <HeroTabIcon scene={s.scene} />
                <span className="hx-tab__label">{s.tabLabel}</span>
                <span className="hx-tab__bar" aria-hidden>
                  {i === active ? <span key={`p-${active}`} className="hx-tab__fill" /> : null}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {leadForm ? <div className="container hx__lead">{leadForm}</div> : null}
    </section>
  );
}
